<template>
  <dashboard
    @onAddPanel="addPanel"
    @onShowPanel="handleTogglePanelVisibilityByType"
    @onInstantPoll="handleShowInstantPollDialog"
  >
    <div slot="panels">
      <draggable
        class="message-panels"
        @start="dragStarted"
        @end="dragEnded"
        ref="message_panels"
        v-model="panelsList"
        v-bind="draggableOptions"
      >
        <panel v-for="el in panelsList" :key="el.id">
          <message-panel
            v-if="isMessagePanel(el.id)"
            :key="el.id"
            :id="el.id"
            :title="el.title"
            :type="panelTypes[el.id]"
            :interactions="interactions(el.id)"
            :onAirUsers="companyUsers"
            :loading="loadingInteractions[el.id]"
            :loadingSuccess="loadingInteractionsSuccess[el.id]"
            :color="panelSettings[el.id].colour"
            :columns="panelSettings[el.id].columns ? panelSettings[el.id].columns.toString() : null"
            :goToLastScrollPosition="goToLastScrollPosition"
            :isPaused="!!panelPaused[el.id]"
            :hasMoreInteractions="panelHasMoreInteractions(el.id)"
            :twitterTrends="twitterTrends"
            :pollStats="pollStats(el.id)"
            :campaigns="campaigns"
            :channels="channels"
            :settings="panelSettings[el.id]"
            :canClose="!['activity'].includes(panelTypes[el.id])"
            :mediaPreload="panelSettings[el.id].mediaPreload"
            :panelLock="panelSettings[el.id].panelLock"
            @closePanel="closePanel(el.id)"
            @loadMore="loadMore(el.id)"
            @showSettings="showSettings(el.id)"
            @pausePanel="pausePanel(el.id)"
            @unpausePanel="unpausePanel(el.id)"
            @resetPanel="resetPanel(el.id)"
            @searchPanel="searchPanelInteractions"
            @removeOnAir="removeOnAir"
            @onOpenProfile="handleOpenProfile"
          />
          <chat v-if="isChatPanel(el.id)" @closePanel="closePanel(el.id)" />
        </panel>
      </draggable>
    </div>
    <div slot="gallery">
      <gallery
        :visible="galleryVisible"
        :images="galleryImages"
        :selectedIndex="galleryIndex"
        @setVisible="setGalleryVisible"
      ></gallery>
    </div>
    <div slot="settings">
      <message-panel-settings
        v-if="panelSettingsDialog.id"
        :visible="panelSettingsDialogVisible"
        :id="panelSettingsDialog.id"
        :name="panelSettingsDialog.name"
        :color="panelSettingsDialog.color"
        :columns="panelSettingsDialog.columns"
        :channelIds="panelSettingsDialog.channelIds"
        :campaignIds="panelSettingsDialog.campaignIds"
        :channels="channels"
        :panelType="panelSettingsDialog.type"
        :mediaPreload="panelSettingsDialog.mediaPreload"
        :panelLock="panelSettingsDialog.panelLock"
        :campaigns="activeCampaigns"
        @setVisible="setPanelSettingsVisible"
        @saveSettings="savePanelSettings"
      ></message-panel-settings>

      <instant-poll
        :channels="channels"
        :visible="instantPollDialogVisible"
        @close="handleCloseInstantPoll"
        @cancel="handleCloseInstantPoll"
        @createPanel="handleCreatePanel"
      />
    </div>

    <div slot="modals">
      <profile-detail-dialog
        v-if="profileDialogVisible && selectedProfile"
        :originalProfile="selectedProfile"
        :updatedProfileMap="updatedProfileMap"
        :profileId="getProfileId(selectedProfile)"
        :profileDialogVisible="profileDialogVisible"
        @onDialogVisibilityChange="handleProfileDialogVisibilityChanged"
        @profileUpdated="handleProfileUpdated"
      />
      <thread-dialog />
    </div>
  </dashboard>
</template>

<script>
import draggable from 'vuedraggable';
import gql from 'graphql-tag';
import moment from 'moment';

import { mapGetters, mapState } from 'vuex';

import Chat from '@/components/chat/Chat';
import Gallery from '@/components/common/Gallery';
import Dashboard from '@/components/interaction/Dashboard';
import MessagePanel from '@/components/interaction/MessagePanel';
import MessagePanelSettings from '@/components/interaction/MessagePanelSettings';
import ThreadDialog from '@/components/interaction/ThreadDialog';
import ProfileDetailDialog from '@/components/profile/ProfileDetailDialog';
import ProfileDialogMixin from '@/components/profile/ProfileDialogMixin';
import CampaignManage from '@/components/campaign/CampaignManage';
import InstantPoll from '@/components/interaction/InstantPoll';
import approveTranscriptMutation from '@/graphql/voiceintelligence/approveTranscriptMutation.gql';
import Panel from '@/components/interaction/Panel';
import * as types from '@/store/modules/interaction/mutation-types';

export default {
  name: 'DashboardHeader',
  mixins: [ProfileDialogMixin],
  components: {
    draggable,

    Chat,
    Gallery,
    Dashboard,
    MessagePanel,
    MessagePanelSettings,
    ThreadDialog,
    Panel,
    ProfileDetailDialog,
    CampaignManage,
    InstantPoll,
    approveTranscriptMutation,
  },
  apollo: {
    $client: 'apiv2',
    $subscribe: {
      onInteraction: {
        query: gql`
          subscription {
            onInteraction {
              id
              campaignId
              channelId
              companyId
              threadId
              dateCreated
              dateUpdated
              direction
              flagBlacklisted
              flagComment
              flagFavourite
              flagFraud
              flagSafe
              flagUnsafe
              flagWinner
              ore
              tags
              type
              inCount
              outCount
              parent {
                id
                campaignId
                channelId
                companyId
                threadId
                dateCreated
                dateUpdated
                direction
                flagBlacklisted
                flagComment
                flagFavourite
                flagFraud
                flagSafe
                flagUnsafe
                flagWinner
                ore
                tags
                type
              }
            }
          }
        `,
        result(response) {
          this.$store.commit(
            `interaction/${types.NEW_INTERACTION_RECEIVED}`,
            response.data.onInteraction,
          );
        },
      },
      onairInteraction: {
        query: gql`
          subscription {
            onairInteraction {
              id
              sentBy {
                firstName
                lastName
              }
              interaction {
                id
                campaignId
                channelId
                companyId
                threadId
                dateCreated
                dateUpdated
                direction
                flagBlacklisted
                flagComment
                flagFavourite
                flagFraud
                flagSafe
                flagUnsafe
                flagWinner
                ore
                tags
                type
                inCount
                outCount
              }
            }
          }
        `,
        result(response) {
          this.$store.commit(
            `interaction/${types.NEW_ONAIR_INTERACTION_RECEIVED}`,
            response.data.onairInteraction,
          );
        },
      },
    },
  },
  data: () => ({
    instantPollDialogVisible: false,
    draggableOptions: {
      animation: 200,
      draggable: '.panel-container',
      dropzoneSelector: '.message-panels',
      handle: '.ibox-tools',
    },
    goToLastScrollPosition: false,
  }),
  computed: {
    ...mapGetters('interaction', ['interactions', 'interactionsOnAir', 'pollStats']),
    ...mapGetters('campaign', ['activeCampaigns', 'instantPollCampaigns']),
    ...mapGetters('channel', ['channels']),
    ...mapState('campaign', ['campaigns']),
    ...mapState('interaction', [
      'loadingInteractions',
      'hasMoreInteractions',
      'loadingInteractionsSuccess',
      'galleryImages',
      'galleryVisible',
      'galleryIndex',
      'panelPaused',
      'panels',
      'panelSettings',
      'panelTypes',
      'panelSettingsDialog',
      'panelSettingsDialogVisible',
      'twitterTrends',
      'instantPollResults',
      'panelPages',
    ]),
    ...mapState('user', ['companyUsers', 'me']),
    panelsList: {
      get() {
        const panelList = Object.keys(this.panels).map(e => ({ id: e, title: this.panels[e] }));
        return panelList;
      },
      set(value) {
        const asObject = value.reduce((acc, cur) => ({ ...acc, [cur.id]: cur.title }), {});
        this.$store.commit(`interaction/${types.UPDATE_PANEL_ORDER_REQUEST}`, asObject);
      },
    },
  },
  mounted() {
    this.$store.dispatch('user/getCompanyUsers');
    this.$store.dispatch('interaction/fetchPanels');
    this.$store.dispatch('interaction/fetchTwitterTrends');
    this.$store.dispatch('interaction/fetchOnAirInteractions', 'onAir');
    this.$store.dispatch('campaign/getCampaigns');
    this.$store.dispatch('channel/getChannels');
  },
  destroyed() {
    this.$store.dispatch('interaction/clearDashboardPanelPaused');
  },
  methods: {
    panelHasMoreInteractions(panelId) {
      return this.hasMoreInteractions[panelId] !== undefined
        ? this.hasMoreInteractions[panelId]
        : true;
    },

    setGalleryVisible(isVisible) {
      this.$store.commit(`interaction/${types.SET_GALLERY_VISIBLE}`, isVisible);
    },

    setPanelSettingsVisible(isVisible) {
      this.$store.commit(`interaction/${types.SET_PANEL_SETTINGS_DIALOG_VISIBLE}`, isVisible);
    },

    panelKeys() {
      return Object.keys(this.panels);
    },

    loadMore(panelId) {
      this.$store.dispatch('interaction/fetchNextPageInteractions', panelId);
    },

    addPanel() {
      this.$store.dispatch('interaction/createPanel');
    },
    reorderPanels() {
      this.$store.dispatch('interaction/reorderPanels', this.panels);
    },

    resetPanel(panelId) {
      this.$store.dispatch('interaction/fetchPanelInteractions', [panelId, null, 1]);
    },

    handleTogglePanelVisibilityByType([type, hidden]) {
      const uuid = this.getPanelIdByType(type);
      const settings = {
        uuid,
        name: this.panels[uuid],
        ...this.panelSettings[uuid],
      };

      this.savePanelSettings(Object.assign({}, settings, { hidden }));
    },

    getPanelIdByType(type) {
      /* eslint-disable no-console */
      console.log(this.panelTypes);
      const panelIds = Object.keys(this.panelTypes);
      return panelIds.filter(x => this.panelTypes[x] === type)[0];
    },

    isMessagePanel(panelId) {
      const isHidden = this.panelSettings[panelId].hidden;
      const isProfilePanel = panelId.includes('profile-panel');
      const isChatPanel = this.panelTypes[panelId] === 'chat';
      return !isHidden && !isProfilePanel && !isChatPanel;
    },

    isChatPanel(panelId) {
      const isHidden = this.panelSettings[panelId].hidden;
      const isChatPanel = this.panelTypes[panelId] === 'chat';
      return !isHidden && isChatPanel;
    },

    closePanel(panelId) {
      const type = this.panelTypes[panelId];

      if (type === 'activity') {
        // Ignore if this is the activity feed.
        return;
      }

      const persistentPanelTypes = ['onair', 'twittertrends', 'chat'];

      if (persistentPanelTypes.includes(type)) {
        this.handleTogglePanelVisibilityByType([type, true]);
      } else {
        this.$store.dispatch('interaction/deletePanel', panelId);
      }
    },

    showSettings(panelId) {
      const settings = {
        id: panelId,
        name: this.panels[panelId],
        color: this.panelSettings[panelId].colour,
        columns: this.panelSettings[panelId].columns,
        type: this.panelTypes[panelId],
        channelIds: this.panelSettings[panelId].channel_ids || [],
        campaignIds: this.panelSettings[panelId].campaign_ids || [],
        mediaPreload: this.panelSettings[panelId].mediaPreload || 'auto',
        panelLock: this.panelSettings[panelId].panelLock || 'auto',
      };
      this.$store.commit(`interaction/${types.SET_PANEL_SETTINGS_DIALOG}`, settings);
      this.$store.commit(`interaction/${types.SET_PANEL_SETTINGS_DIALOG_VISIBLE}`, true);
    },

    savePanelSettings(panel) {
      this.$store.dispatch('interaction/updatePanelSettings', panel);
      this.setPanelSettingsVisible(false);
    },

    dragStarted() {
      this.goToLastScrollPosition = false;
    },

    dragEnded() {
      this.goToLastScrollPosition = true;
      this.reorderPanels();
    },

    unpausePanel(panelId) {
      this.$store.dispatch('interaction/unpausePanelInteractions', panelId);
      this.handleInstantPollPanel(panelId);
    },

    pausePanel(panelId) {
      this.$store.dispatch('interaction/pausePanelInteractions', panelId);
    },

    removeOnAir([panelId, interactionId, onAirId]) {
      this.$store.dispatch('interaction/removeOnAirInteraction', [panelId, interactionId, onAirId]);
      this.unapproveTranscript(onAirId);
    },

    searchPanelInteractions(panelId, nextPage, searchStr) {
      this.$store.dispatch('interaction/searchPanelInteractions', [panelId, nextPage, searchStr]);
    },

    handleOpenProfile(originatorType, originator) {
      this.$store.dispatch('profile/searchProfileForIdentity', [originatorType, originator]);
    },

    handleShowInstantPollDialog() {
      this.instantPollDialogVisible = true;
    },

    async handleCreatePanel([name, camapaignIds, channelIds]) {
      const panelSettings = {
        name,
        instant_poll: true,
        campaign_ids: camapaignIds,
        channel_ids: channelIds,
      };
      await this.$store.dispatch('interaction/createPanel', panelSettings);
      await this.$store.dispatch('campaign/getCampaigns');
      this.instantPollDialogVisible = false;
    },

    getInstantPoll(panelSettings, panelId) {
      if (!('campaign_ids' in panelSettings)) return null;

      // instant polls panels only have one campaign
      if (panelSettings.campaign_ids.length !== 1) return null;

      // check if sole campaign is attached to an instant poll
      if (!this.instantPollCampaigns.includes(panelSettings.campaign_ids[0])) {
        return null;
      }

      const instantPoll = this.campaigns.find(
        campaign => campaign.id === panelSettings.campaign_ids[0],
      );

      // check if the campaign is past the reporting time
      if (moment().diff(moment(instantPoll.end_date), 'hours') > 3) {
        this.closePanel(panelId);
      }

      return instantPoll;
    },

    handleInstantPollPanel(panelId) {
      if (this.panelSettings[panelId].instant_poll === true) {
        this.getInstantPoll(this.panelSettings[panelId], panelId);
      }
    },

    handleCloseInstantPoll() {
      this.instantPollDialogVisible = false;
    },

    unapproveTranscript(interactionId) {
      this.$apollo.mutate({
        client: 'apiv2',
        mutation: approveTranscriptMutation,
        variables: {
          query: {
            interactionId: String(interactionId),
            approveStatus: false,
            approvedById: this.me.id,
            approvedBy: `${this.me.first_name} ${this.me.last_name}`,
            approveReason: '',
          },
        },
      });
    },

    addingInstagramChannel() {},
    addingTwitterChannel() {},
    handleCreateCampaignVisibilityChanged() {},
    handleRequestTwitterAuth() {},
    handleRequestInstagramAuth() {},
    handleCampaignCreated() {},
  },
};
</script>



// WEBPACK FOOTER //
// DashboardContainer.vue?2048a912